import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { SEARCH_DEBOUNCE_MS } from '~/common/constants/debounces';
import EcBooleanTextSearchPopup from '~/components/Shared/EcBooleanTextSearchPopup/EcBooleanTextSearchPopup';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Text } from '~/eds/src/components/Text/Text';
import { Tooltip } from '~/eds/src/components/Tooltip/Tooltip';
import { useDebounce } from '~/eds/src/hooks/useDebounce';
import { api } from '~/redux/api/createApi';
import Box from '~/ui/components/Box/Box';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import { TextInput } from '~/ui/components/TextInput/TextInput.typed';

interface ValidationQueryError {
  response?: {
    data?: {
      detail?: string;
    };
  };
}

interface Props {
  includeOperatorButton?: boolean;
  isDisabled?: boolean;
  isSubmitting?: boolean;
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onValidationError?: (error?: string) => void;
}

const BaseBooleanSearchBar = ({
  includeOperatorButton = true,
  isDisabled,
  isSubmitting,
  value,
  onChange,
  onSubmit,
  onValidationError,
}: Props) => {
  const [showSyntaxPopup, setShowSyntaxPopup] = useState(false);
  const intl = useIntl();

  const [validateBooleanQuery, validationQueryResult] =
    api.endpoints.validateBooleanQuery.useLazyQuery();
  const validateQuery = useDebounce(
    (query: string) => {
      validateBooleanQuery(query);
    },
    SEARCH_DEBOUNCE_MS,
    [validateBooleanQuery],
  );

  const booleanQueryError = (
    validationQueryResult.error as unknown as ValidationQueryError | undefined
  )?.response?.data?.detail;
  const booleanQuery = value;
  const isInputValueInvalid = booleanQuery && booleanQueryError;
  const handleBooleanInputChange = onChange;

  const handleSubmit = () => {
    if (!isInputValueInvalid) {
      onSubmit();
    }
  };

  const renderSearchInputAppendix = () => {
    if (!isInputValueInvalid && includeOperatorButton && booleanQuery.length) {
      const keyToPressName =
        navigator.platform.toUpperCase().indexOf('MAC') >= 0
          ? intl.formatMessage({
              id: 'BaseBooleanSearchBar.returnKey',
              defaultMessage: 'Return',
              description: 'Mac keyboard return key name',
            })
          : intl.formatMessage({
              id: 'BaseBooleanSearchBar.enterKey',
              defaultMessage: 'Enter',
              description: 'PC keyboard enter key name',
            });
      return (
        <Box mt={1}>
          <Text color="text.secondary" variant="tiny">
            <FormattedMessage
              id="BaseBooleanSearchBar.pressKeyToSearch"
              defaultMessage="Press <strong>{keyName}</strong> to run search."
              description="Instruction to press a key to run search"
              values={{
                strong: (chunks) => <strong>{chunks}</strong>,
                keyName: keyToPressName,
              }}
            />
          </Text>
        </Box>
      );
    } else {
      return null;
    }
  };

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      inputRef.current?.focus();
    }, 500);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (booleanQuery) {
      validateQuery(booleanQuery);
    }
  }, [booleanQuery, validateQuery]);

  useEffect(() => {
    onValidationError?.(booleanQueryError);
  }, [booleanQueryError, onValidationError]);

  return (
    <Box sx={{ width: '100%' }} mb={4}>
      <FlexLayout flexDirection="column">
        <FlexLayout>
          <Box sx={{ width: '100%' }}>
            <Tooltip
              tooltip={
                isDisabled
                  ? intl.formatMessage({
                      id: 'BaseBooleanSearchBar.editInAnalyzerTooltip',
                      defaultMessage:
                        'Edit in Analyzer to add a Boolean Text Search',
                      description: 'Tooltip for disabled search input',
                    })
                  : null
              }
            >
              <TextInput
                error={isInputValueInvalid}
                ref={inputRef}
                placeholder={intl.formatMessage({
                  id: 'BaseBooleanSearchBar.searchPlaceholder',
                  defaultMessage: 'Search filenames and content',
                  description: 'Placeholder text for search input',
                })}
                value={booleanQuery}
                onChange={handleBooleanInputChange}
                onEnter={handleSubmit}
                disabled={isDisabled || isSubmitting}
                width="fullWidth"
                flexGrow={1}
              />
            </Tooltip>
          </Box>
          {includeOperatorButton && (
            <IconButton
              icon="info"
              tooltip={intl.formatMessage({
                id: 'BaseBooleanSearchBar.searchOperatorTooltip',
                defaultMessage: 'Search Operator Reference Guide',
                description: 'Tooltip for search operator info button',
              })}
              onClick={() => setShowSyntaxPopup(true)}
            />
          )}
        </FlexLayout>
        {renderSearchInputAppendix()}
      </FlexLayout>
      {showSyntaxPopup && (
        <EcBooleanTextSearchPopup
          handleOnCloseClick={() => setShowSyntaxPopup(false)}
        />
      )}
    </Box>
  );
};

export default BaseBooleanSearchBar;
